/**
 * JobActivityLog — AISC §1.7.0 Phase 4 chronological feed of all JOB-* events
 * for a job (RFIs, transmittals, PIT, waivers, jobs.* direct changes).
 *
 * URL: /jobs/:job_id/activity   OR   /job-activity?job_id=:id
 *
 * Read-only. Backed by GET /api/job/:id/activity (Phase 4 jobActivity route).
 * audit_log is V-03 trigger-protected — append-only at DB.
 */

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert, Box,
  TextField, MenuItem, Breadcrumbs, Link as MuiLink,
} from "@mui/material";
import { HomeRounded, ArrowBackRounded, RefreshRounded, WarningAmberRounded } from "@mui/icons-material";

import { getJobActivity, describeEvent } from "../../utils/jobActivityApi";
import { fmtDateTimeUTC } from "../../utils/dateFmt";

const SOURCE_COLOR = {
  // Legacy field-level diff schema source_table values (rolled-back design)
  jobs:                 { bg: "#e3f2fd", color: "#0d47a1" },
  rfis:                 { bg: "#fff3cd", color: "#664d03" },
  transmittals:         { bg: "#e1bee7", color: "#4a148c" },
  print_issue_tickets:  { bg: "#b3e5fc", color: "#01579b" },
  customer_waivers:     { bg: "#c8e6c9", color: "#1b5e20" },
  // 2026-05-06 — production domain-event schema object_type values
  // (lowercased by the SQL: LOWER(COALESCE(object_type, 'event'))).
  job:                  { bg: "#e3f2fd", color: "#0d47a1" },
  task:                 { bg: "#dbeafe", color: "#1e3a8a" },
  piece:                { bg: "#ecfccb", color: "#365314" },
  heat:                 { bg: "#fef3c7", color: "#78350f" },
  label:                { bg: "#fce7f3", color: "#831843" },
  document:             { bg: "#e0e7ff", color: "#3730a3" },
  bolt_test:            { bg: "#cffafe", color: "#155e75" },
  event:                { bg: "#e9ecef", color: "#495057" },
};

const EVENT_TYPE_OPTIONS = [
  { value: "", label: "All events" },
  { value: "AISC-1-7-0:JOB-RFI", label: "RFIs" },
  { value: "AISC-1-7-0:JOB-TRANSMITTAL", label: "Transmittals" },
  { value: "AISC-1-7-0:JOB-PIT", label: "Print Issue Tickets" },
  { value: "AISC-1-7-0:JOB-WAIVER", label: "Customer Waivers" },
  { value: "AISC-1-7-0", label: "Direct job changes (category / waiver)" },
];

export default function JobActivityLog() {
  const navigate = useNavigate();
  // The route is registered as `/jobs/:id/activity-log` (see src/routes.js),
  // so the route param key is `id`, NOT `job_id`. Pre-2026-05-05 this
  // component destructured `{ job_id }` which always returned undefined,
  // making the page render "No job_id provided" for every visit.
  // Fix: read both common names so the component is resilient to future
  // route renames + the existing query-param entry path.
  const params = useParams();
  const [searchParams] = useSearchParams();
  const jobId =
    params.id ||
    params.job_id ||
    params.jobId ||
    searchParams.get("job_id") ||
    searchParams.get("id");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState("");

  const refresh = () => {
    if (!jobId) { setLoading(false); setError("No job_id provided"); return; }
    setLoading(true); setError(null); setNotAvailable(false);
    const filters = { limit: 500 };
    if (eventTypeFilter) filters.event_type = eventTypeFilter;
    getJobActivity(jobId, filters)
      .then((data) => setRows((data && data.rows) || []))
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 404 || status === 501) setNotAvailable(true);
        else setError(err?.response?.data?.error || err?.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [jobId, eventTypeFilter]);

  return (
    <>
      <Helmet><title>Job Activity Log | SFM</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}><HomeRounded fontSize="small" /> <span>Home</span></Stack>
          </MuiLink>
          {jobId && <MuiLink href={`/jobs/${jobId}`} color="inherit" underline="hover">Job #{jobId}</MuiLink>}
          <Typography color="text.primary">Activity Log</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button startIcon={<ArrowBackRounded />} onClick={() => navigate(jobId ? `/jobs/${jobId}` : "/jobs")}>Back</Button>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Activity Log {jobId && <Typography component="span" sx={{ color: "text.secondary", fontSize: "0.6em", ml: 1 }}>Job #{jobId}</Typography>}
            </Typography>
          </Stack>
          <Button variant="outlined" startIcon={<RefreshRounded />} onClick={refresh}>Refresh</Button>
        </Stack>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          AISC §1.7.0 Phase 4 — chronological feed of all JOB-level events. V-03 trigger-protected (append-only at DB).
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <TextField select label="Event type" size="small" sx={{ minWidth: 280 }}
            value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)}>
            {EVENT_TYPE_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
          </TextField>
        </Paper>

        {notAvailable && <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>Phase 4 backend (jobActivity route) hasn't landed.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && <Paper sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>Loading…</Paper>}

        {!loading && rows.length === 0 && !error && !notAvailable && (
          <Paper sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
            No activity recorded yet for this job.
          </Paper>
        )}

        {!loading && rows.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Box sx={{ position: "relative", pl: 3 }}>
              <Box sx={{ position: "absolute", left: 8, top: 8, bottom: 8, width: 2, background: "#dee2e6" }} />
              {rows.map((ev, i) => {
                const colors = SOURCE_COLOR[ev.source_table] || { bg: "#e9ecef", color: "#495057" };
                const friendly = describeEvent(ev.reason_category);
                return (
                  <Box key={i} sx={{ position: "relative", pb: 2 }}>
                    <Box sx={{
                      position: "absolute", left: -22, top: 4, width: 12, height: 12,
                      borderRadius: "50%", background: colors.color,
                      border: "2px solid #fff", boxShadow: "0 0 0 1px #dee2e6"
                    }} />
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.3 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {fmtDateTimeUTC(ev.created_at_utc)}
                      </Typography>
                      <Chip size="small" label={ev.source_table.replace(/_/g, " ")}
                        sx={{ backgroundColor: colors.bg, color: colors.color, fontSize: "0.65rem", fontWeight: 600 }} />
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {friendly} <Typography component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>(#{ev.source_id})</Typography>
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#495057", fontSize: "0.85rem" }}>
                      {ev.field_name && (ev.previous_value !== ev.new_value) && (
                        <span><strong>{ev.field_name}:</strong> {ev.previous_value || "(null)"} → {ev.new_value || "(null)"}. </span>
                      )}
                      {ev.reason_comment && <span style={{ fontStyle: "italic" }}>"{ev.reason_comment}"</span>}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      By user #{ev.actor_user_id} ({ev.actor_role || "—"})
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}
      </Container>
    </>
  );
}
